import React, {useContext, useEffect, useMemo, useState} from 'react';
import {PAContext} from "../../../Services/PAContext";
import {
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    Paper,
    Grid,
    Alert,
    Snackbar,
    ToggleButtonGroup,
    ToggleButton,
    Select,
    MenuItem,
    FormControl,
    ListSubheader,
    Switch,
    FormControlLabel,
    Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CategoryIcon from '@mui/icons-material/Category';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';



function Essentials(){


    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account } = dataArray;

    console.log("Account.essentials: ", Account.essentials);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    /**
     * Core State: A flat list of item objects.
     * Each item: { id: string, text: string, category: 'Food'|'Supplies', sub_category: string }
     */
    const [essentials, setEssentials] = useState(Account.essentials || []);


    // Selection State
    const [selectedCategory, setSelectedCategory] = useState('supplies');
    const [selectedSub, setSelectedSub] = useState('');

    // Editing State
    const [editingItem, setEditingItem] = useState(null);

    // Creation State
    const [showNewSubField, setShowNewSubField] = useState(false);
    const [newSubTitle, setNewSubTitle] = useState('');
    const [newItemName, setNewItemName] = useState('');
    const [newItemNotes, setNewItemNotes] = useState('');
    const [newItemIsPrivate, setNewItemIsPrivate] = useState(false);


    /**
     * Derived State: Get unique subcategories for the selected category from the flat list
     */
    const subcategoriesForCategory = useMemo(() => {
        console.log("selectedCategory, essentials got triggered : ", essentials);
        const subs = new Set();
        essentials.forEach(item => {
            if (item.category === selectedCategory) {
                subs.add(item.sub_category);
            }
        });
        return Array.from(subs).sort((a, b) => a.localeCompare(b));
    }, [selectedCategory, essentials]);

    // Ensure a valid subcategory is selected if one exists and current is empty
    useMemo(() => {
        if (!selectedSub && subcategoriesForCategory.length > 0) {
            setSelectedSub(subcategoriesForCategory[0]);
        }
    }, [subcategoriesForCategory, selectedSub]);

    const handleAddItem = () => {
        if (!newItemName.trim() || !selectedSub) return;

        const newItem = {
            id: Date.now(), // Matching the numeric ID style from your example
            name: newItemName.trim(),
            category: selectedCategory.toLowerCase(),
            sub_category: selectedSub,
            notes: newItemNotes.trim(),
            private: newItemIsPrivate,
            status: 1
        };

        setEssentials(prev => [...prev, newItem]);

        // Reset inputs
        setNewItemName('');
        setNewItemNotes('');
        setNewItemIsPrivate(false);
    };

    const handleUpdateItem = () => {
        if (!editingItem) return;

        setEssentials(prev => prev.map(item =>
            item.id === editingItem.id ? editingItem : item
        ));
        setEditingItem(null);
        setSnackbar({ open: true, message: 'Item updated!', severity: 'success' });
    };

    const handleAddSubcategory = () => {
        const title = newSubTitle.trim();
        if (!title) {
            setShowNewSubField(false);
            return;
        }

        // Check if subcategory already exists for this category in the items list
        const exists = essentials.some(item =>
            item.category === selectedCategory &&
            item.sub_category.toLowerCase() === title.toLowerCase()
        );

        if (exists) {
            setSnackbar({ open: true, message: 'Subcategory already exists!', severity: 'error' });
            return;
        }

        // To "create" a subcategory in a flat list without an item,
        // we just set the selection to it so the first item added will use it.
        setSelectedSub(title);
        setNewSubTitle('');
        setShowNewSubField(false);
    };

    const handleDeleteItem = (itemId) => {
        setEssentials(prev => prev.filter(item => item.id !== itemId));
    };

    function handleSaveAccountEssentials() {
        // In a real app, you'd POST the `essentials` array to your API here
        console.log("Saving Essentials Array:", essentials);

    }




    return (
        <Box>

            {/* SAVE Button */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2}}>
                <Button
                    variant="contained"
                    startIcon={<i className="icon-floppy-disk" />}
                    onClick={handleSaveAccountEssentials}
                    sx={{ bgcolor: '#f09637', '&:hover': { bgcolor: '#d9822b' } }}
                >
                    Save Essentials
                </Button>
            </Box>

            <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a1a' }}>Essentials Manager</Typography>
                <Typography variant="body1" color="text.secondary">Manage your categorized inventory list.</Typography>
            </Box>

            {/* Quick Add Bar */}
            <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4, borderRadius: '16px', bgcolor: '#fcfcfc' }}>
                <Grid container spacing={2}>
                    {/* Selectors Row */}
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, mb: 0.5, display: 'block', color: 'text.secondary' }}>1. CATEGORY</Typography>
                        <ToggleButtonGroup
                            value={selectedCategory}
                            exclusive
                            onChange={(e, val) => {
                                if (val) {
                                    setSelectedCategory(val);
                                    setSelectedSub('');
                                }
                            }}
                            size="small"
                            fullWidth
                            sx={{ bgcolor: 'white' }}
                        >
                            <ToggleButton value="Food" sx={{ fontWeight: 600 }}>Food</ToggleButton>
                            <ToggleButton value="Supplies" sx={{ fontWeight: 600 }}>Supplies</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, mb: 0.5, display: 'block', color: 'text.secondary' }}>2. SUBCATEGORY</Typography>
                        {showNewSubField ? (
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                placeholder="Name new sub..."
                                value={newSubTitle}
                                onChange={(e) => setNewSubTitle(e.target.value)}
                                onBlur={handleAddSubcategory}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddSubcategory()}
                                sx={{ bgcolor: 'white' }}
                            />
                        ) : (
                            <FormControl fullWidth size="small">
                                <Select
                                    value={selectedSub}
                                    displayEmpty
                                    onChange={(e) => e.target.value === 'ADD_NEW' ? setShowNewSubField(true) : setSelectedSub(e.target.value)}
                                    sx={{ bgcolor: 'white' }}
                                    renderValue={(selected) => {
                                        if (!selected) return <span style={{ color: '#aaa' }}>Select subcategory</span>;
                                        return selected;
                                    }}
                                >
                                    <MenuItem value="ADD_NEW" sx={{ color: '#f09637', fontWeight: 800 }}>
                                        <AddIcon fontSize="small" sx={{ mr: 1 }} /> NEW SUBCATEGORY
                                    </MenuItem>
                                    {subcategoriesForCategory.length > 0 && (
                                        <ListSubheader sx={{ lineHeight: '24px', fontSize: '10px', fontWeight: 900 }}>EXISTING</ListSubheader>
                                    )}
                                    {subcategoriesForCategory.map(opt => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Grid>

                    {/* Item Details Row */}
                    <Grid item xs={12} sm={5}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, mb: 0.5, display: 'block', color: 'text.secondary' }}>3. ITEM NAME</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="e.g. Toothpaste"
                            disabled={!selectedSub}
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                            sx={{ bgcolor: 'white' }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, mb: 0.5, display: 'block', color: 'text.secondary' }}>NOTES (OPTIONAL)</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Add specifics..."
                            disabled={!selectedSub}
                            value={newItemNotes}
                            onChange={(e) => setNewItemNotes(e.target.value)}
                            sx={{ bgcolor: 'white' }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                        <Box sx={{ display: 'flex', gap: 1, height: '40px' }}>
                            <Tooltip title="Private Item">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={newItemIsPrivate}
                                            onChange={(e) => setNewItemIsPrivate(e.target.checked)}
                                            color="warning"
                                        />
                                    }
                                    label={<LockIcon sx={{ fontSize: 18, color: newItemIsPrivate ? 'warning.main' : 'text.disabled' }} />}
                                    sx={{ m: 0 }}
                                />
                            </Tooltip>
                            <Button
                                variant="contained"
                                onClick={handleAddItem}
                                disabled={!selectedSub || !newItemName.trim()}
                                sx={{ bgcolor: '#1a1a1a', flexGrow: 1, borderRadius: '8px', '&:hover': { bgcolor: '#000' } }}
                            >
                                <AddIcon />
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Display Lists grouped by Category and Subcategory */}
            <Grid container spacing={4}>
                {['food', 'supplies'].map((category) => {
                    const categoryItems = essentials.filter(i => i.category === category);
                    const categorySubs = Array.from(new Set(categoryItems.map(i => i.sub_category))).sort();

                    return (
                        <Grid item xs={12} md={6} key={category}>
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, borderBottom: '2px solid #f09637' }}>
                                {category === 'Food' ? <RestaurantIcon sx={{ color: '#f09637' }} /> : <ShoppingBagOutlinedIcon sx={{ color: '#f09637' }} />}
                                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.02em' }}>
                                    {category.toUpperCase()}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {categorySubs.map((sub) => (
                                    <Box key={sub}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <CategoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                {sub}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 2 }}>
                                            {categoryItems
                                                .filter(i => i.sub_category === sub)
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map((item) => (
                                                    <Tooltip
                                                        key={item.id}
                                                        title={
                                                            <Box sx={{ p: 0.5 }}>
                                                                <Typography variant="caption" display="block" sx={{ fontWeight: 700, mb: 0.5 }}>Click to Edit</Typography>
                                                                {item.notes && <Typography variant="caption" display="block"><NotesIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} /> {item.notes}</Typography>}
                                                                {item.private && <Typography variant="caption" display="block"><LockIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} /> Private</Typography>}
                                                            </Box>
                                                        }
                                                        arrow
                                                    >
                                                        <Chip
                                                            label={item.name}
                                                            onClick={() => setEditingItem({ ...item })}
                                                            onDelete={() => handleDeleteItem(item.id)}
                                                            variant="outlined"
                                                            size="small"
                                                            icon={item.private ? <LockIcon style={{ fontSize: '14px' }} /> : undefined}
                                                            sx={{
                                                                borderRadius: '8px',
                                                                fontWeight: 500,
                                                                bgcolor: item.private ? '#fff7ed' : 'white',
                                                                borderColor: item.private ? '#f09637' : 'divider',
                                                                '& .MuiChip-deleteIcon': { color: '#d32f2f' },
                                                                '&:hover': { bgcolor: item.private ? '#ffedd5' : '#f5f5f5' }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                ))}
                                        </Box>
                                    </Box>
                                ))}

                                {categoryItems.length === 0 && (
                                    <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ddd', borderRadius: '12px' }}>
                                        <Typography variant="caption" color="text.disabled">
                                            No items added to {category}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Edit Item Dialog */}
            <Dialog
                open={Boolean(editingItem)}
                onClose={() => setEditingItem(null)}
                PaperProps={{ sx: { borderRadius: '16px', width: '100%', maxWidth: '400px' } }}
            >
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon sx={{ color: '#f09637' }} /> Edit Item
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <TextField
                            label="Item Name"
                            fullWidth
                            size="small"
                            value={editingItem?.name || ''}
                            onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                        />
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            value={editingItem?.notes || ''}
                            onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editingItem?.private || false}
                                    onChange={(e) => setEditingItem({ ...editingItem, private: e.target.checked })}
                                    color="warning"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LockIcon sx={{ fontSize: 18, color: editingItem?.private ? 'warning.main' : 'text.disabled' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Private Item</Typography>
                                </Box>
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setEditingItem(null)} sx={{ color: 'text.secondary', fontWeight: 600 }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdateItem}
                        sx={{ bgcolor: '#f09637', fontWeight: 700, borderRadius: '8px', '&:hover': { bgcolor: '#d9822b' } }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>


            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%', borderRadius: '12px', fontWeight: 600 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default Essentials;